import i18n from "../../i18n";
import React, { useMemo } from "react";
import { Stack, Text, List } from "@mantine/core";
import _ from "lodash";
import { useCellRender } from "../../hooks/useCellRender";
import { getMappableFieldByPath } from "../../data/orderServiceMappableFields";
import {
  isFileServiceFieldType,
  isMultiFileServiceFieldType,
  isRefServiceFieldType,
} from "../../data/orderServiceFieldTypes";
import { formatOrderStatusLabel } from "../../data/options";

const formatStatus = (value) => formatOrderStatusLabel(value);

const formatValue = (def, value, cellRender) => {
  if (value === null || value === undefined || value === "") return "-";

  if (def.orderFieldPath === "address" && typeof value === "object") {
    return [value.line1, value.line2, value.city, value.state, value.zip, value.country]
      .filter((part) => part && String(part).trim())
      .join(", ");
  }

  if (isFileServiceFieldType(def.fieldType)) {
    const files = Array.isArray(value) ? value : [value];
    if (!files.length) return "-";
    return files
      .map((file) => file?.name || file?.filename || file?.originalName || "File")
      .join(", ");
  }

  if (def.fieldType === "DATE") {
    return cellRender.Date(value, "LL");
  }

  if (def.fieldType === "BOOLEAN") {
    return value ? "Yes" : "No";
  }

  if (def.fieldType === "SELECT_ORDER_STATUS") {
    return formatStatus(value);
  }

  if (def.fieldType === "SELECT") {
    const option = (def.selectOptions || []).find(
      (o) => (o.value ?? o.label) === value
    );
    if (option) {
      return option.cname ? `${option.label} / ${option.cname}` : option.label;
    }
  }

  if (isRefServiceFieldType(def.fieldType)) {
    return String(value);
  }

  if (typeof value === "object") {
    return JSON.stringify(value);
  }

  return String(value);
};

const ServiceTypeFieldsDisplay = ({ form }) => {
  const [cellRender] = useCellRender();
  const definitions = useMemo(() => {
    const fields =
      form?.values?.serviceTypeSnapShot?.fields ||
      form?.values?.serviceType?.fields ||
      [];
    return [...fields].sort(
      (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
    );
  }, [form?.values]);

  const fieldValues = form?.values?.fieldValues || {};

  if (!definitions.length) return null;

  return (
    <Stack spacing="xs" mt="md">
      <Text size="sm" weight={600}>{i18n.t("Service Fields")}</Text>
      <List spacing="xs" size="sm">
        {definitions.map((def) => {
          const label = def.cname ? `${def.label} / ${def.cname}` : def.label;
          return (
            <List.Item key={def.key}>
              <Text size="sm" weight={500}>
                {label}
              </Text>
              <Text size="sm">
                {formatValue(def, fieldValues[def.key], cellRender)}
              </Text>
              {isFileServiceFieldType(def.fieldType) ? (
                <Text size="xs" color="dimmed">
                  Stored on service record only
                </Text>
              ) : null}
            </List.Item>
          );
        })}
      </List>
    </Stack>
  );
};

export default ServiceTypeFieldsDisplay;
